import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getAvatarColor, getInitials } from '@/utils/avatarUtils'
import { cn } from '@/utils/cn'
import type { PresenceViewer } from '@/types/presence'

const MAX_VISIBLE = 4
const MAX_OVERFLOW_NAMES = 8

interface PlanPresenceAvatarsProps {
  viewers: PresenceViewer[]
  currentVersionId: string | undefined
  versions?: { id: string; alias: string }[]
}

function viewerLabel(
  viewer: PresenceViewer,
  currentVersionId: string | undefined,
  versions: { id: string; alias: string }[] | undefined
) {
  if (viewer.versionId === currentVersionId) return viewer.name || viewer.email
  const alias = versions?.find(v => v.id === viewer.versionId)?.alias
  return `${viewer.name || viewer.email} — on ${alias ?? 'a different version'}`
}

export function PlanPresenceAvatars({
  viewers,
  currentVersionId,
  versions,
}: PlanPresenceAvatarsProps) {
  if (viewers.length === 0) return null

  const ordered = [
    ...viewers.filter(v => v.versionId === currentVersionId),
    ...viewers.filter(v => v.versionId !== currentVersionId),
  ]

  const overflowCount = Math.max(ordered.length - (MAX_VISIBLE - 1), 0)
  const visible =
    overflowCount > 0 ? ordered.slice(0, MAX_VISIBLE - 1) : ordered
  const overflow = overflowCount > 0 ? ordered.slice(MAX_VISIBLE - 1) : []

  return (
    <TooltipProvider>
      <div className="flex items-center">
        {visible.map(viewer => {
          const color = getAvatarColor(viewer.userId)
          return (
            <Tooltip key={viewer.userId}>
              <TooltipTrigger asChild>
                <Avatar className="h-7 w-7 -ml-2 ring-2 ring-background first:ml-0">
                  <AvatarFallback
                    className={cn(color.bg, color.text, 'text-xs font-medium')}
                  >
                    {getInitials(viewer.name, viewer.email)}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                {viewerLabel(viewer, currentVersionId, versions)}
              </TooltipContent>
            </Tooltip>
          )
        })}
        {overflow.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex h-7 w-7 -ml-2 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground ring-2 ring-background">
                +{overflow.length}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-56 whitespace-normal text-left">
              {overflow
                .slice(0, MAX_OVERFLOW_NAMES)
                .map(v => viewerLabel(v, currentVersionId, versions))
                .join(', ')}
              {overflow.length > MAX_OVERFLOW_NAMES &&
                ` and ${overflow.length - MAX_OVERFLOW_NAMES} more`}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  )
}
